import { NumberFormat } from '../presentation-values.mjs';
import { scalarSchema } from '../models/value.mjs';
import { requireExport } from '../export/errors.mjs';

// DES1's existing artifact-content namespace. Labels are resolved by the
// trusted renderer in its interface locale, never persisted in NumberFormat.
export const NUMBER_FORMAT_NULL_KEYS = Object.freeze({
  'source-null': 'valueSourceNull',
  missing: 'valueMissing',
  redacted: 'valueRedacted',
});
export const NUMBER_FORMAT_NULL_DASH = '\u2014';

function exactIntlAvailable() {
  try {
    const options = {
      useGrouping: false,
      minimumFractionDigits: 20,
      maximumFractionDigits: 20,
      roundingMode: 'halfEven',
    };
    const decimal = new Intl.NumberFormat('en-US', options);
    const percent = new Intl.NumberFormat('en-US', { ...options, style: 'percent' });
    const ties = new Intl.NumberFormat('en-US', {
      ...options,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return (
      decimal.resolvedOptions().roundingMode === 'halfEven' &&
      decimal.format('9007199254740993.01000000000000000001') ===
        '9007199254740993.01000000000000000001' &&
      percent.format('90071992547409.9301000000000000000001') ===
        '9007199254740993.01000000000000000001%' &&
      ties.format('1.005') === '1.00' &&
      ties.format('-1.005') === '-1.00'
    );
  } catch {
    return false;
  }
}

// Intl v3 keeps a decimal string as an exact mathematical value. Older engines
// may silently coerce it to binary float, or ignore display rounding. Refuse
// those engines rather than returning a plausible, lossy preview.
const exactIntl = exactIntlAvailable();

function nullText(value, format, options) {
  if (format.nullStyle === 'blank') return '';
  if (format.nullStyle === 'dash') return NUMBER_FORMAT_NULL_DASH;
  const label = options?.nullLabels?.[value.reason];
  requireExport(
    typeof label === 'string' && label.length > 0 && label.length <= 1000,
    'unsupported-presentation-export',
  );
  return label;
}

function assertMetadata(value, format) {
  if (format.style === 'currency')
    requireExport(format.currency === value.currency, 'unsupported-presentation-export');
  if (format.style === 'unit')
    requireExport(
      format.unit === value.unit && value.currency === null,
      'unsupported-presentation-export',
    );
  if (format.style === 'percent')
    requireExport(
      value.currency === null && value.unit === 'ratio',
      'unsupported-presentation-export',
    );
}

/** Inputs are the sole ART scalar and INS formatter representations. ART owns
 * registered slot/ratio admission. Display never rewrites values or metadata. */
export function formatExactNumberFormat(actualScalar, numberFormat, options) {
  const scalar = scalarSchema.safeParse(actualScalar);
  const parsed = NumberFormat.safeParse(numberFormat);
  requireExport(scalar.success && parsed.success, 'type-error');
  const value = scalar.data,
    format = parsed.data;
  if (value.kind === 'null') return nullText(value, format, options);
  requireExport(value.kind === 'decimal', 'type-error');
  assertMetadata(value, format);
  requireExport(exactIntl, 'unsupported-presentation-export');
  const { locale, nullStyle: _nullStyle, scale: _scale, ...intlOptions } = format;
  const formatter = new Intl.NumberFormat(locale, {
    ...intlOptions,
    roundingMode: value.rounding === 'half-even' ? 'halfEven' : 'halfExpand',
  });
  requireExport(
    formatter.resolvedOptions().roundingMode ===
      (value.rounding === 'half-even' ? 'halfEven' : 'halfExpand'),
    'unsupported-presentation-export',
  );
  return formatter.format(value.value);
}

/** Bind existing interface-locale i18n labels once to ART/UI1's two-arg port.
 * Null reasons stay on the original scalar for aria/alt, muted styling and
 * column alignment. XLSX/CSV use true empty cells, not this display string. */
export function createNumberFormatPresenter({ nullLabels } = {}) {
  const labels = nullLabels ? Object.freeze({ ...nullLabels }) : undefined;
  return (scalar, format) => formatExactNumberFormat(scalar, format, { nullLabels: labels });
}
